import VueCookie from 'vue-cookie'

// configs
import configs from '@/config'

import store from '@/vuex/store'
import util from '@/util'
// Leaf module of plain constants — no import-cycle risk (see ppt-online.utils.js).
import officeConstants from '@/constant/office'

const initOptions = {
  expires: 365,
  sameSite: 'none',
  secure: true,
}

const otherRegions = ['eu']

const handleGetGoogleSlideToken = (key) => {
  return window.localStorage.getItem(key) || ''
}

// Named to match general-api's services/aha-token.js, which writes them.
export const AHA_TOKEN_COOKIE = 'ahaToken'
export const AHA_LOGGED_IN_COOKIE = 'ahaLoggedIn'
export const AHA_USER_ID_COOKIE = 'ahaUserId'
// The FE-written user id, distinct from the server's AHA_USER_ID_COOKIE.
export const FE_USER_ID_COOKIE = 'aha-user-id'

// Every other cookie name the app writes, kept here so a rename lands in one place.
export const FIRST_PAGE_COOKIE = 'ahaFirstPage'
export const NEW_USER_COOKIE = 'aha-new-user'
export const REFERRER_COOKIE = 'referrer'
export const PLAN_KEY_COOKIE = 'aha_plan_key'
export const PRICING_VERSION_COOKIE = 'aha_pricing_version'
export const QUIZ_INFO_TOP_COOKIE = 'showQuizInfoTop'
export const HAS_FOLDER_COOKIE = 'hasFolder'
export const AUTH_PENDING_COOKIE = 'is-auth-pending'
export const SSO_REDIRECT_COOKIE = 'audience-sso-login-redirect'

// The API sets these un-prefixed on every region, so a prefixed read looks for a key nothing writes.
// Set by general-api for Google One Tap (signup marker; existing account's email for the
// login page). The server knows no region prefix.
export const AHA_ONE_TAP_SIGNUP_COOKIE = 'ahaOneTapSignup'
export const AHA_ONE_TAP_LOGIN_EMAIL_COOKIE = 'ahaOneTapLoginEmail'
const UNPREFIXED_KEYS = new Set([
  AHA_TOKEN_COOKIE, AHA_LOGGED_IN_COOKIE, AHA_USER_ID_COOKIE, AHA_ONE_TAP_SIGNUP_COOKIE, AHA_ONE_TAP_LOGIN_EMAIL_COOKIE,
])

const transformKey = (key) => {
  if (!key || UNPREFIXED_KEYS.has(key)) return key

  let prefix = ''

  if (otherRegions.includes(configs.region)) {
    prefix += `${configs.region}-`
  }

  if (prefix) {
    return prefix + key
  }

  return key
}

const _getRootDomain = (domain) => {
  if (!domain) {
    return null
  }
  if (domain.includes('.ngrok')) {
    return domain
  }
  if (!util.checkWhiteLabelDomain() || configs.env !== 'local') {
    const parts = domain.split('.')
    return parts.slice(-2).join('.')
  }

  return domain
}

const setValueByDomain = (key, value, options = initOptions) => {
  if (!window.location.hostname.includes(configs.domain)) return

  if (configs.domain === 'ahaslides.eu') return

  const newOptions = {
    ...options,
    domain: configs.domain,
  }

  const newKey = transformKey(key)
  VueCookie.set(newKey, value || '', newOptions)
}

const setValue = (key, value, options = initOptions) => {
  if (store.state.isEnableAddinPowerpoint) {
    const parts = [`${key}=${value}`, 'path=/']
    // tiny-cookie reads a numeric `expires` as days, so the raw write must match that unit.
    if (Number.isInteger(options?.expires)) {
      parts.push(`max-age=${options.expires * 24 * 60 * 60}`)
    }
    // PowerPoint Online hosts the task pane in a cross-site iframe (the top-level
    // document is office.com), and a cookie that doesn't say SameSite=None; Secure
    // defaults to Lax — which the browser refuses to write from a third-party frame.
    // This branch bypasses VueCookie, so it has to carry those attributes over from
    // `options` itself; dropping them means the write is silently discarded (AHA-43111).
    //
    // Scoped to Online on purpose: the desktop webview is a first-party context that
    // has always shipped without these attributes, so it is left byte-for-byte as it
    // was. Both halves of the check are race-free by the time any cookie is written —
    // `isEnableAddinPowerpoint` is a build-time process.env value, and
    // `addinPPTPlatform` is committed from Office.context.platform on every add-in
    // load (services/office captureOfficePlatform), before sign-in can complete.
    if (configs.isEnableAddinPowerpoint
      && store.state.addinPPTPlatform === officeConstants.OFFICE_ONLINE) {
      const sameSite = options.sameSite || initOptions.sameSite
      parts.push(`SameSite=${sameSite}`)
      // SameSite=None is only honoured on a Secure cookie; browsers drop it otherwise.
      if (options.secure || String(sameSite).toLowerCase() === 'none') {
        parts.push('Secure')
      }
    }
    document.cookie = parts.join('; ')
    return
  }
  const newKey = transformKey(key)

  VueCookie.set(newKey, value || '', options)
}

const getValue = (key) => {
  if (store?.state?.isEnableAddinPowerpoint) {
    const name = `${key}=`
    const decodedCookie = decodeURIComponent(document.cookie)
    const ca = decodedCookie.split(';')
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i]
      while (c.charAt(0) == ' ') {
        c = c.substring(1)
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length)
      }
    }
    return ''
  }
  if (configs.integrationPlatform === 'GoogleSlide') {
    return handleGetGoogleSlideToken(key)
  }
  const newKey = transformKey(key)
  const value = VueCookie.get(newKey)
  if (value === 'true') {
    return true
  }

  if (value === 'false') {
    return false
  }

  return value
}

const handleRemoveGoogleSlideToken = (key) => {
  window.localStorage.removeItem(key)
}

const deleteValue = (key) => {
  const newKey = transformKey(key)
  if (configs.integrationPlatform === 'GoogleSlide') {
    handleRemoveGoogleSlideToken(key)
  }
  VueCookie.delete(newKey, {
    path: '/',
    sameSite: 'none',
    secure: true,
  })

  if (window.location.hostname.includes(configs.domain)) {
    VueCookie.delete(newKey, {
      domain: configs.domain,
      path: '/',
      sameSite: 'none',
      secure: true,
    })
  }
}

const deleteTokenValue = (key = AHA_TOKEN_COOKIE) => {
  deleteValue(key)

  if (util.isEuOrWhiteLabel()) return

  const newKey = transformKey(key)

  VueCookie.delete(newKey, {
    domain: _getRootDomain(configs.domain),
    path: '/',
    sameSite: 'none',
    secure: true,
  })
}

const deleteCookieGoogleSlide = (name) => {
  handleRemoveGoogleSlideToken(name)
}

const setRootDomainCookie = (key, value, options = {}) => {
  VueCookie.set(key, value, {
    ...initOptions,
    ...options,
    domain: _getRootDomain(configs.domain),
    path: '/',
  })
}

const deleteRootDomainCookie = (key, options = {}) => {
  VueCookie.delete(key, {
    domain: _getRootDomain(configs.domain),
    path: '/',
    sameSite: 'none',
    secure: true,
    ...options,
  })
}

export default {
  deleteValue,
  setValue,
  setValueByDomain,
  getValue,
  deleteTokenValue,
  deleteCookieGoogleSlide,
  setRootDomainCookie,
  deleteRootDomainCookie,
}
